import React, { useEffect, useRef } from 'react';
import Appear from '../../components/ui/Appear';
import Tooltip from '../../components/ui/Tooltip';
import { AuthorityChip } from '../Authority';
import { NODE_SWATCH } from '../DocSection';
import type { PlanNode } from '../types';
import { useCanvas, useCanvasBundle, useNodeBox } from './canvasStore';
import { DRAG_THRESHOLD, capture, swallowNextClick } from './events';
import type { Side, XY } from './geometry';
import { clampGroupSize } from './groups';
import { useRuntime } from './runtime';
import { useCardDrag } from './useCardDrag';

interface CanvasGroupProps {
  node: PlanNode;
  /** Nesting depth: a deeper bubble paints over the one holding it. */
  depth: number;
  /** May move and resize this group: a manager, or its authority, on a writable plan. */
  canEdit: boolean;
  /** Which handles start an arrow: both for a manager, the outgoing one for an authority. */
  arrows: 'both' | 'out' | 'none';
  /** How much changed here since this person last looked. */
  unread: number;
}

interface Resize {
  pointerId: number;
  startClient: XY;
  startFlow: XY;
  size: { width: number; height: number };
  active: boolean;
}

/**
 * Groups paint under the arrows' layer, which has no z-index, so an arrow into a card
 * inside a bubble stays visible and clickable. Depth orders bubbles among themselves.
 */
const GROUP_Z_FLOOR = -100;

/** Two diagonal grip bars on a fill-less root: the house icon construction. */
const GRIP = 'M14 7v2l-5 5H7zM14 11v2l-1 1h-2z';

/** A group on the canvas: a tinted bubble behind what it holds, with a title bar that reads like a card. */
const CanvasGroup: React.FC<CanvasGroupProps> = ({ node, depth, canEdit, arrows, unread }) => {
  const { id } = node;
  const rt = useRuntime();
  const bundle = useCanvasBundle();
  // Trap 7: this group subscribes to its own box, selected flag and drop flag, nothing else.
  const box = useNodeBox(id);
  const selected = useCanvas((s) => s.selection.has(id));
  const dropping = useCanvas((s) => s.dropTargets.has(id));
  const el = useRef<HTMLDivElement | null>(null);
  const drag = useCardDrag(id, canEdit, el);
  const resize = useRef<Resize | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Shift on a bubble draws a marquee over what it holds, as on empty space.
    if (e.shiftKey) return;
    drag.onPointerDown(e);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      rt.openPanel(id, true);
    } else if (e.key === ' ') {
      e.preventDefault();
      rt.selection.clickNode(id, e);
    }
  };

  // ── The corner handle: live size in the store, one save on release ──

  const endResize = (r: Resize) => {
    resize.current = null;
    el.current?.classList.remove('np-resizing');
    if (r.active) bundle.endMove([id]);
  };

  // Unmounted mid-resize: release the moving guard, or the next reload could never size this id.
  useEffect(
    () => () => {
      const r = resize.current;
      if (r) endResize(r);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onResizeDown = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (!box || e.pointerType === 'touch' || e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    capture(e.currentTarget, e.pointerId);
    resize.current = {
      pointerId: e.pointerId,
      startClient: { x: e.clientX, y: e.clientY },
      startFlow: rt.viewport.clientToFlow(e.clientX, e.clientY),
      size: { width: box.width, height: box.height },
      active: false,
    };
  };

  const onResizeMove = (e: React.PointerEvent<HTMLSpanElement>) => {
    const r = resize.current;
    if (!r || e.pointerId !== r.pointerId) return;
    e.stopPropagation();
    if (!r.active) {
      if (Math.hypot(e.clientX - r.startClient.x, e.clientY - r.startClient.y) < DRAG_THRESHOLD) return;
      r.active = true;
      // A reload landing mid-resize must not snap the bubble back to its saved size.
      bundle.beginMove([id]);
      el.current?.classList.add('np-resizing');
    }
    const p = rt.viewport.clientToFlow(e.clientX, e.clientY);
    bundle.store.getState().resizeNode(
      id,
      clampGroupSize({ width: r.size.width + p.x - r.startFlow.x, height: r.size.height + p.y - r.startFlow.y }),
    );
  };

  const onResizeUp = (e: React.PointerEvent<HTMLSpanElement>) => {
    const r = resize.current;
    if (!r || e.pointerId !== r.pointerId) return;
    e.stopPropagation();
    endResize(r);
    if (!r.active) return;
    swallowNextClick();
    rt.saveSize(id);
  };

  const onResizeCancel = (e: React.PointerEvent<HTMLSpanElement>) => {
    const r = resize.current;
    if (!r || e.pointerId !== r.pointerId) return;
    endResize(r);
    if (r.active) bundle.store.getState().resizeNode(id, r.size);
  };

  if (!box) return null;

  const handle = (side: Side) => {
    const live = arrows === 'both' || (arrows === 'out' && side === 'right');
    return (
      <span
        aria-hidden="true"
        data-handle-side={side}
        className={`np-handle np-handle-${side} ${live ? 'np-handle-live' : ''}`}
        onPointerDown={live ? (e) => rt.connect.onHandlePointerDown(e, id, side) : undefined}
      />
    );
  };

  return (
    <div
      ref={el}
      data-node-id={id}
      data-group=""
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`${node.title}, group${unread > 0 ? `, ${unread} new` : ''}`}
      className={`np-group ${canEdit ? 'np-node-draggable' : ''} ${dropping ? 'np-group-drop' : ''}`}
      style={{
        transform: `translate(${box.x}px, ${box.y}px)`,
        width: box.width,
        height: box.height,
        zIndex: GROUP_Z_FLOOR + depth,
        ['--np-group-tint' as any]: node.color === 'none' ? undefined : NODE_SWATCH[node.color],
        ['--np-group-edge' as any]: node.color === 'none' ? undefined : '35%',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerUp}
      onPointerCancel={drag.onPointerCancel}
      onClick={(e) => {
        // A plain click or tap opens the panel; a multi-select click only toggles.
        if (e.ctrlKey || e.metaKey) rt.selection.clickNode(id, e);
        else rt.openPanel(id, false);
      }}
      onKeyDown={onKeyDown}
    >
      <div className="np-group-bar">
        <span className="np-group-title" title={node.title}>
          {node.title}
        </span>
        <AuthorityChip name={node.authority_name} />
        <Appear show={unread > 0} className="np-pill flex-shrink-0 tabular-nums">
          <span
            // Inline: np-pill sets the quiet color, and a class here would lose to it on load order.
            style={{ color: 'var(--on-surface)', fontWeight: 600 }}
            title={`${unread} new since you last looked`}
          >
            {unread} new
          </span>
        </Appear>
      </div>
      {handle('left')}
      {handle('right')}
      {canEdit && (
        <Tooltip label="Drag to resize">
          <span
            aria-hidden="true"
            className="np-group-resize"
            onPointerDown={onResizeDown}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeUp}
            onPointerCancel={onResizeCancel}
            onClick={(e) => e.stopPropagation()}
          >
            <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
              <path d={GRIP} fill="currentColor" />
            </svg>
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default React.memo(CanvasGroup);
